import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export type AccountType = 
  | "revenue"
  | "deduction"
  | "variable_cost"
  | "fixed_expense"
  | "financial_expense"
  | "other";

export interface ChartAccount {
  id: string;
  restaurant_id: string;
  parent_id: string | null;
  code: string;
  name: string;
  account_type: AccountType;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ChartAccountTree extends ChartAccount {
  children: ChartAccountTree[];
  level: number;
}

export interface CreateAccountInput {
  parent_id?: string | null;
  code: string;
  name: string;
  account_type: AccountType;
  sort_order?: number;
}

export interface UpdateAccountInput {
  name?: string;
  code?: string;
  parent_id?: string | null;
  account_type?: AccountType;
  sort_order?: number;
  is_active?: boolean;
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  revenue: "Receita",
  deduction: "Dedução",
  variable_cost: "Custo Variável",
  fixed_expense: "Despesa Operacional",
  financial_expense: "Despesa Financeira",
  other: "Outros",
};

export function useChartOfAccounts() {
  const { activeRestaurant } = useRestaurantContext();
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    if (!activeRestaurant) {
      setAccounts([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("chart_of_accounts" as any)
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("code");

      if (error) throw error;
      setAccounts((data as unknown as ChartAccount[]) || []);
    } catch (e) {
      console.error("Error fetching chart of accounts", e);
      toast.error("Erro ao carregar plano de contas");
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  // Initialize default accounts for existing restaurants
  const initializeAccounts = useCallback(async () => {
    if (!activeRestaurant) return;

    try {
      const { error } = await supabase.rpc("create_default_chart_of_accounts" as any, {
        p_restaurant_id: activeRestaurant.id,
      });

      if (error) throw error;
      await fetchAccounts();
    } catch (e) {
      console.error("Error initializing chart of accounts", e);
    }
  }, [activeRestaurant, fetchAccounts]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Build tree structure from flat list
  const buildTree = useCallback((flatAccounts: ChartAccount[]): ChartAccountTree[] => {
    const accountMap = new Map<string, ChartAccountTree>();
    const roots: ChartAccountTree[] = [];

    // First pass: create all nodes
    flatAccounts.forEach((account) => {
      accountMap.set(account.id, { ...account, children: [], level: 0 });
    });

    // Second pass: build tree
    flatAccounts.forEach((account) => {
      const node = accountMap.get(account.id)!;
      if (account.parent_id && accountMap.has(account.parent_id)) {
        const parent = accountMap.get(account.parent_id)!;
        node.level = parent.level + 1;
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort children by sort_order then code
    const sortNodes = (nodes: ChartAccountTree[]) => {
      nodes.sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code));
      nodes.forEach((node) => sortNodes(node.children));
    };
    sortNodes(roots);

    return roots;
  }, []);

  const accountTree = buildTree(accounts);

  // Flatten tree for display (with levels)
  const flattenTree = useCallback((tree: ChartAccountTree[]): ChartAccountTree[] => {
    const result: ChartAccountTree[] = [];
    const traverse = (nodes: ChartAccountTree[]) => {
      nodes.forEach((node) => {
        result.push(node);
        traverse(node.children);
      });
    };
    traverse(tree);
    return result;
  }, []);

  const flatTree = flattenTree(accountTree);

  const createAccount = useCallback(
    async (input: CreateAccountInput): Promise<string | null> => {
      if (!activeRestaurant) return null;

      try {
        const { data, error } = await supabase
          .from("chart_of_accounts" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            parent_id: input.parent_id || null,
            code: input.code,
            name: input.name,
            account_type: input.account_type,
            sort_order: input.sort_order || 0,
            is_system: false,
          })
          .select("id")
          .single();

        if (error) {
          if (error.code === "23505") {
            toast.error("Código já existe. Use outro código.");
            return null;
          }
          throw error;
        }

        toast.success("Conta criada com sucesso");
        await fetchAccounts();
        return (data as any)?.id || null;
      } catch (e) {
        console.error("Error creating account", e);
        toast.error("Erro ao criar conta");
        return null;
      }
    },
    [activeRestaurant, fetchAccounts]
  );

  const updateAccount = useCallback(
    async (id: string, input: UpdateAccountInput): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("chart_of_accounts" as any)
          .update(input)
          .eq("id", id);

        if (error) {
          if (error.code === "23505") {
            toast.error("Código já existe. Use outro código.");
            return false;
          }
          throw error;
        }

        toast.success("Conta atualizada com sucesso");
        await fetchAccounts();
        return true;
      } catch (e) {
        console.error("Error updating account", e);
        toast.error("Erro ao atualizar conta");
        return false;
      }
    },
    [fetchAccounts]
  );

  const toggleAccountActive = useCallback(
    async (id: string, isActive: boolean): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("chart_of_accounts" as any)
          .update({ is_active: isActive })
          .eq("id", id);

        if (error) throw error;

        toast.success(isActive ? "Conta ativada" : "Conta desativada");
        await fetchAccounts();
        return true;
      } catch (e) {
        console.error("Error toggling account status", e);
        toast.error("Erro ao alterar status da conta");
        return false;
      }
    },
    [fetchAccounts]
  );

  // Get only leaf accounts (no children) for linking to expense categories
  const getLeafAccounts = useCallback(
    (type?: AccountType): ChartAccount[] => {
      const childIds = new Set(accounts.filter((a) => a.parent_id).map((a) => a.parent_id));
      return accounts.filter(
        (a) =>
          !childIds.has(a.id) &&
          a.is_active &&
          (!type || a.account_type === type)
      );
    },
    [accounts]
  );

  return {
    accounts,
    accountTree,
    flatTree,
    isLoading,
    refetch: fetchAccounts,
    initializeAccounts,
    createAccount,
    updateAccount,
    toggleAccountActive,
    getLeafAccounts,
  };
}
